import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Grid } from '@mui/material';
import { apiService } from '@/utils/api';
import { Card } from '@/components/ui/Card';
import { DeviceSelector as DeviceSelectorNew } from '@/components/shared/DeviceSelectorNew';
import { ClipLoader } from "react-spinners";
import { FiActivity, FiHome, FiToggleRight, FiClock, FiSettings, FiSliders, FiDroplet, FiWifi, FiThermometer, FiPower, FiChevronDown, FiChevronUp, FiChevronRight } from 'react-icons/fi';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@/store';
import { motion } from 'framer-motion';
import { CircularProgressbar, CircularProgressbarWithChildren, buildStyles } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';
import { Sidebar, ChemistrySidebar, PumpSpeedSidebar, TemperatureSidebar } from '../../components/ui/Sidebar';
import ScheduleCard from '@/components/ui/ScheduleCard';
import { AuxTypeCode } from '@/utils/deviceColorUtils';
import { DeviceCard } from '@/components/ui/DeviceCard/deviceCard';
import useGlobalDeviceSelector from '@/hooks/useGlobalDeviceSelector';
import { DashboardResponse,AuxAssign } from '@/types/dashboard'
import { Chemistry, ChlorineSettings, PhSettings} from '@/types/chemistry';
import { setLoading } from '@/store/slices/appSlice';
import FiltrationCard from '@/components/dashboard/FiltrationCard';
import { getMotorStatusText, isMotorRunning, getMotorStatusColor } from '@/types/motorStatus';
import { hasAuxType, getPhColor, getOrpColorByDesired } from '@/utils/chemistryUtils';
import { isVSDPump } from '@/utils/utils';

const DashboardPage: React.FC = () => {
  const { mode } = useSelector((state: RootState) => state.theme);
  const { selectedDeviceId, onDeviceChange } = useGlobalDeviceSelector();
  const [dashboardData, setDashboardData] = useState<DashboardResponse | null>(null);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<number | null>(null);
  const [expandedDeviceIds, setExpandedDeviceIds] = useState<number[]>([]);
  const [phColor, setPhColor] = useState<string>('#3B82F6'); // Default blue
  const [orpColor, setOrpColor] = useState<string>('#10B981'); // Default green
 const [isScheduleExpanded, setIsScheduleExpanded] = useState<boolean>(false);
 const dispatch = useDispatch();

  const hasLiquidChlorine = hasAuxType(dashboardData?.auxAssign, AuxTypeCode.LIQUID_CHLORINE) || false;

  // Add state for sidebar management
  const [activeSidebar, setActiveSidebar] = useState<string | null>(null);
  const [sidebarLoading, setSidebarLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!selectedDeviceId) return;

    // const fetchDeviceOverview = async () => {
    //   try {
    //     const data = await apiService.get<DeviceOverview>(`/api/v1/sensor-data/overview?device_id=${selectedDeviceId}`);
    //     setOverview(data);
    //   } catch (err) {
    //     console.error('Error fetching device overview:', err);
    //   }
    // };

    const fetchDashboardData = async () => {
      // Start loading
      try {
        // 使用新的API端点
        const response = await apiService.get<DashboardResponse>(`/api/v1/iot-shadow/device/${selectedDeviceId}/dashboardOverview`);

        // 直接设置新的数据结构
        setDashboardData(response);
        setLastUpdatedAt(Date.now());

        // 更新pH和ORP颜色
        if (response.dashboard.actual_ph !== undefined) {
          setPhColor(getPhColor(response.dashboard.actual_ph / 10));
        }

        if (response.dashboard.actual_orp !== undefined) {
          const useLiquidDesired = hasAuxType(response.auxAssign, AuxTypeCode.LIQUID_CHLORINE);
          const desiredOrp = useLiquidDesired ? response.dashboard.d_orp_liquid : response.dashboard.d_orp_mineral;
          setOrpColor(getOrpColorByDesired(response.dashboard.actual_orp, desiredOrp, response.dashboard.is_naked));
        }
      } catch (err) {
        //这设置一个空的dashboardData
        console.error('Error fetching dashboard data:', err);
      } finally {
        dispatch(setLoading(false)); // End loading regardless of success or failure
      }
    };
    dispatch(setLoading(true)); 
    // fetchDeviceOverview();
    fetchDashboardData();
    // dispatch(setLoading(false));

    const interval = setInterval(() => {
      // fetchDeviceOverview();
      fetchDashboardData();
    }, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, [selectedDeviceId]);

  // 设备在线状态
  const [deviceOnlineStatus, setDeviceOnlineStatus] = useState<boolean>(false);

  // 定时获取设备在线状态
  useEffect(() => {
    if (!selectedDeviceId) return;

    // 立即获取一次设备在线状态
    fetchDeviceOnlineStatus();

    // 设置定时器，每30秒获取一次设备在线状态
    const intervalId = setInterval(() => {
      fetchDeviceOnlineStatus();
    }, 30000); // 30秒

    return () => {
      clearInterval(intervalId);
    };
  }, [selectedDeviceId]);

  // 获取设备在线状态
  const fetchDeviceOnlineStatus = () => {
    if (!selectedDeviceId) return;

    apiService.get<{ onlineStatus: boolean }>(`/api/v1/iot-shadow/device/${selectedDeviceId}/onlineStatus`)
      .then(response => {
        setDeviceOnlineStatus(response.onlineStatus);
      })
      .catch(error => {
        console.error('Failed to fetch device online status:', error);
      });
  };

  const isDeviceOnline = (): boolean => {
    return deviceOnlineStatus;
  };

  // Get appropriate icon for aux device type
  const getAuxIcon = (type: number) => {
    switch (type) {
      case 5: // Salt Chlorinator
      case 8: // Liquid Chlorine
        return <FiDroplet className="text-xl text-primary-500" />;
      case 6: // pH Doser
        return <FiSliders className="text-xl text-primary-500" />;
      case 13: // Pool Lights
      case 29: // Spa Lights
        return <FiPower className="text-xl text-primary-500" />;
      case 17: // Solar
      case 32: // Heater
        return <FiThermometer className="text-xl text-primary-500" />;
      case 20: // SPA Actuator Return
      case 21: // SPA Actuator Suction
      case 22: // SPA Jet
        return <FiToggleRight className="text-xl text-primary-500" />;
      default:
        return <FiSettings className="text-xl text-primary-500" />;
    }
  };

  // Toggle expanded state for a device
  const toggleDeviceExpanded = (id: number) => {
    console.log('toggleDeviceExpanded：', id, expandedDeviceIds);
    if (expandedDeviceIds.includes(id)) {
      setExpandedDeviceIds(expandedDeviceIds.filter(deviceId => deviceId !== id));
    } else {
      setExpandedDeviceIds([...expandedDeviceIds, id]);
    }
  };

  // Function to update pH color based on value
  const updatePhColor = (value: number) => {
    setPhColor(getPhColor(value));
  };

  // Function to update ORP color based on value
  const updateOrpColor = (value: number, expectedValue: number) => {
    setOrpColor(getOrpColorByDesired(value, expectedValue, dashboardData?.dashboard.is_naked));
  };

  const [chemistryData, setChemistryData] = useState<{
    chemistry: Chemistry;
    chlorineSettings: ChlorineSettings;
    phSettings: PhSettings;
    auxAssign: AuxAssign;
  } | null>(null);

  // Toggle sidebar
  const toggleSidebar = (sidebarId: string) => {
    const opening = activeSidebar !== sidebarId;
    if (opening && sidebarId === 'chemistry' && selectedDeviceId) {
      setSidebarLoading(true);
      // Fetch chemistry data when opening chemistry sidebar
      apiService.get<{
        chemistry: Chemistry;
        chlorineSettings: ChlorineSettings;
        phSettings: PhSettings;
        auxAssign: AuxAssign;
      }>(`/api/v1/iot-shadow/device/${selectedDeviceId}/chemistryCard`)
        .then(response => {
          setChemistryData(response);
        })
        .catch(error => {
          console.error('Failed to fetch chemistry data:', error);
        })
        .finally(() => setSidebarLoading(false));
    }
    setActiveSidebar(opening ? sidebarId : null);
  };

  // Close sidebar
  const closeSidebar = () => {
    setActiveSidebar(null);
    setChemistryData(null);
    setSidebarLoading(false);
  };

  return (
    <div className="page-container relative h-full flex flex-auto flex-col px-4 sm:px-6 md:px-8 py-4 sm:py-6 md:px-8">
      <div className="flex flex-col gap-4 h-full">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <FiHome className="text-primary-500 text-xl" />
              <h2 className={classNames(
                "text-lg font-semibold",
                {
                  'text-gray-900': mode === 'light',
                  'text-gray-200': mode === 'dark'
                }
              )}>Dashboard</h2>
            </div>
            <p className={classNames(
              "mt-1 text-sm",
              {
                'text-gray-500': mode === 'light',
                'text-gray-400': mode === 'dark'
              }
            )}>
              Monitor your devices and system status
            </p>
          </div>
          <div className="flex items-center gap-2 mr-16">
            <DeviceSelectorNew
              onDeviceChange={useCallback((deviceId: string) => {
                setDashboardData(null);
                onDeviceChange(deviceId);
              }, [onDeviceChange])}
              selectedDeviceId={selectedDeviceId}
            />
          </div>
        </div>
        <div className="h-0.5 bg-gray-200 dark:bg-gray-700 my-4"></div>

        {/* Device Status Card */}
        <Card>
          <div>
            <div className="flex items-center justify-between">
              <div>
                <h5 className="font-semibold">Device Status</h5>
                <p className="text-sm text-gray-500">
                  Last updated: {lastUpdatedAt ? new Date(lastUpdatedAt).toLocaleString() : 'N/A'}
                </p>
              </div>
              <div className={`flex items-center gap-2 px-3 py-1 rounded-full ${isDeviceOnline() ? 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400' : 'bg-red-100 text-red-600'
                }`}>
                {isDeviceOnline() && (
                  <div className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                  </div>
                )}
                {isDeviceOnline() ? 'Online' : 'Offline'}
              </div>
            </div>

            {dashboardData?.networkInfo && (
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="flex items-center gap-3 p-3 bg-gray-50/70 dark:bg-gray-800/50 rounded-lg backdrop-blur-sm">
                  <FiWifi className="text-xl text-primary-500" />
                  <div className="flex-grow">
                    <p className="font-medium">
                      {dashboardData.networkInfo.status === "Ethernet"
                        ? "Ethernet"
                        : dashboardData.networkInfo.network_name}
                    </p>
                    <p className="text-sm text-gray-500">
                      {'Connected'}
                    </p>
                  </div>
                  {dashboardData.networkInfo.status === "Ethernet" ? (
                    <div className="flex flex-col items-center justify-center">
                      <div className="flex items-end h-5 gap-1">
                        {/* 以太网连接显示满格信号 */}
                        {Array.from({ length: 4 }).map((_, index) => (
                          <div
                            key={index}
                            className="w-1.5 rounded-sm bg-primary-500 dark:bg-primary-400"
                            style={{ height: `${(index + 1) * 3 + 2}px` }}
                          />
                        ))}
                      </div>

                    </div>
                  ) : dashboardData.networkInfo.signal !== "" && (
                    <div className="flex flex-col items-center justify-center">
                      <div className="flex items-end h-5 gap-1">
                        {/* WiFi strength bars - 4 bars total */}
                        {Array.from({ length: 4 }).map((_, index) => {
                          // 处理格式为 "RSSI-42" 的信号强度
                          const signalString = dashboardData.networkInfo.signal;
                          const signalMatch = typeof signalString === 'string' ? signalString.match(/RSSI-(\d+)/) : null;
                          const signalStrength = signalMatch ? -parseInt(signalMatch[1], 10) :
                            (typeof signalString === 'number' ? signalString : -100);

                          const threshold = -50 - (index * 10); // -50, -60, -70, -80

                          return (
                            <div
                              key={index}
                              className={`w-1.5 rounded-sm ${signalStrength >= threshold
                                  ? 'bg-primary-500 dark:bg-primary-400'
                                  : 'bg-gray-200 dark:bg-gray-600'
                                }`}
                              style={{ height: `${(index + 1) * 3 + 2}px` }}
                            />
                          );
                        })}
                      </div>
                      <span className="text-xs text-gray-500 mt-1">
                        {typeof dashboardData.networkInfo.signal === 'string'
                          ? dashboardData.networkInfo.signal.replace('RSSI', '')
                          : dashboardData.networkInfo.signal} dBm
                      </span>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-3 p-3 bg-gray-50/70 dark:bg-gray-800/50 rounded-lg backdrop-blur-sm">
                  <FiSettings className="text-xl text-primary-500" />
                  <div>
                    <p className="font-medium">Firmware</p>
                    <p className="text-sm text-gray-500">
                      {dashboardData.versionInfo.firmware_version}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-gray-50/70 dark:bg-gray-800/50 rounded-lg backdrop-blur-sm">
                  <FiClock className="text-xl text-primary-500" />
                  <div>
                    <p className="font-medium">Time Zone</p>
                    <p className="text-sm text-gray-500">
                      {dashboardData.timeInfo.time_zone_id}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </Card>

        {/* Main Pump Card */}
        {(
          <Card>
            <div>
              <div className="flex items-center justify-between mb-3">
                <h5 className="font-semibold">Main Pump</h5>
                {/* Find main pump schedule */}
                {(() => {
                  const mainPumpSchedule = dashboardData?.scheduleInfo.schedules.find(s => s.aux_slot === 0);
                  if (mainPumpSchedule) {
                    return (
                      <div className="flex items-center">
                        <button
                          onClick={() => toggleSidebar('pumpSpeed')}
                          className={classNames(
                            "p-1.5 rounded-full transition-colors",
                            {
                              "hover:bg-gray-100 text-gray-500": mode === 'light',
                              "hover:bg-gray-800 text-gray-400": mode === 'dark'
                            }
                          )}
                          aria-label="View pump speed settings"
                        >
                          {/* <FiChevronRight size={18} /> */}
                        </button>
                      </div>
                    );
                  }
                  // return (
                  //   <button 
                  //     onClick={() => toggleSidebar('pumpSpeed')}
                  //     className={classNames(
                  //       "p-1.5 rounded-full transition-colors",
                  //       {
                  //         "hover:bg-gray-100 text-gray-500": mode === 'light',
                  //         "hover:bg-gray-800 text-gray-400": mode === 'dark'
                  //       }
                  //     )}
                  //     aria-label="View pump speed settings"
                  //   >
                  //     <FiChevronRight size={18} />
                  //   </button>
                  // );
                })()}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="flex items-center gap-3 p-3 bg-gray-50/70 dark:bg-gray-800/50 rounded-lg backdrop-blur-sm">
                  <FiSettings className="text-xl text-primary-500" />
                  <div>
                    <p className="text-sm text-gray-500">
                      {dashboardData?.pump.pump_type} - {dashboardData?.pump.pump_brand} {dashboardData?.pump.pump_model}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-3 bg-gray-50/70 dark:bg-gray-800/50 rounded-lg backdrop-blur-sm">
                  <FiSliders className="text-xl text-primary-500" />
                  <div>
                    <p className="font-medium">Pump Speed</p>
                    <p className="text-sm text-gray-500">
                      {dashboardData?.pump.actual_speed}%
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-3 bg-gray-50/70 dark:bg-gray-800/50 rounded-lg backdrop-blur-sm">
                  <FiActivity className="text-xl text-primary-500" />
                  <div>
                    <p className="font-medium">Flow Rate</p>
                    <p className="text-sm text-gray-500">
                      {dashboardData?.pump.actual_flowrate || 0} L/min
                    </p>
                  </div>
                </div>

                {/*main pump的开关状态 */}
                <div className="flex items-center p-4 bg-white dark:bg-gray-800 rounded-lg shadow-sm">
                  <div className="flex items-center">
                    <span className="flex items-center justify-center w-10 h-10 rounded-full bg-green-100 dark:bg-green-900/30">
                      <FiToggleRight className="h-5 w-5 text-green-600 dark:text-green-400" />
                    </span>
                    <div className="ml-3">
                      <div className="text-sm font-medium">
                        {dashboardData?.manual?.aux.some(aux =>
                          aux.aux_type_code === AuxTypeCode.SPALSHME_PUMP && aux.aux_trump === true
                        ) ? 'Manual' : 'Scheduled'}
                      </div>
                      <div className={`text-xs ${getMotorStatusColor(dashboardData?.pump?.motor_status || 0)} dark:${getMotorStatusColor(dashboardData?.pump?.motor_status || 0)}`}>
                        {getMotorStatusText(dashboardData?.pump?.motor_status || 0)}
                      </div>
                    </div>
                  </div>
                </div>

              </div>

              {/* Main Pump Schedule Section */}
              {(() => {
                // Filter main pump schedules from the new API response structure
                const mainPumpSchedules = dashboardData?.scheduleInfo?.schedules ?
                  dashboardData.scheduleInfo.schedules
                    .filter(schedule => schedule.aux_type_code === AuxTypeCode.SPALSHME_PUMP)
                    .map(schedule => ({
                      ID: schedule.schedule_id,
                      DeviceID: selectedDeviceId || '',
                      Index: schedule.schedule_num,
                      Name: schedule.schedule_name,
                      AuxType: schedule.aux_type_code,
                      AuxSlot: schedule.aux_slot,
                      IsScheduleOn: schedule.schedule_state,
                      StartTime: schedule.start_time,
                      EndTime: schedule.end_time,
                      weekdays_array: schedule.week_days
                    })) : [];

                if (mainPumpSchedules.length === 0) {
                  return null;
                }

                return (
                  <div className="mt-4">
                    <button
                      onClick={() => setIsScheduleExpanded(!isScheduleExpanded)}
                      className="w-full flex items-center justify-between py-2 px-1 text-left border-t border-gray-200/50 dark:border-gray-700/30"
                    >
                      <div className="flex items-center">
                        <FiClock className="text-lg text-primary-500 mr-2" />
                        <span className="font-medium">Schedules</span>
                      </div>
                      {isScheduleExpanded ? <FiChevronUp /> : <FiChevronDown />}
                    </button>

                    {isScheduleExpanded && (
                      <ScheduleCard
                        schedules={mainPumpSchedules}
                        deviceAuxType={AuxTypeCode.SPALSHME_PUMP}
                      />
                    )}
                  </div>
                );
              })()}
            </div>
          </Card>
        )}


        {/* Temperature and Chemistry Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Filtration Card */}
          {dashboardData?.filtration && dashboardData?.pump && (
            <div className="col-span-1 md:col-span-2 mb-4">
              <FiltrationCard
                dashboard={dashboardData.dashboard}
                filtrationData={dashboardData.filtration}
                pumpType={dashboardData.pump.pump_type}
                firmwareVersion={dashboardData.versionInfo?.firmware_version}
              />
            </div>
          )}

          {/* Temperature Card */}
          <Card className="group">
            <div>
              <div className="flex items-center justify-between mb-3">
                <h5 className="font-semibold">Temperature</h5>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => toggleSidebar('temperature')}
                    className={classNames(
                      "p-1.5 rounded-full transition-colors",
                      {
                        "hover:bg-gray-100 text-gray-500": mode === 'light',
                        "hover:bg-gray-800 text-gray-400": mode === 'dark'
                      }
                    )}
                    aria-label="View temperature details"
                  >
                    <FiChevronRight size={18} className="transition-transform group-hover:translate-x-1" />
                  </button>
                </div>
              </div>

              {dashboardData?.dashboard &&
                (dashboardData.dashboard.water_temp !== undefined ||
                  dashboardData.ambientTemp.ambient_temp !== undefined) ? (
                <>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex flex-col items-center gap-3 justify-center p-4 rounded-xl border border-blue-200/50 bg-blue-50/70 dark:bg-blue-900/20 backdrop-blur-sm">
                    <FiThermometer className="h-6 w-6 text-blue-600" />
                    <span className="text-xm font-medium text-gray-500 dark:text-gray-400">Water</span>
                    {dashboardData.dashboard.water_temp !== undefined ? (
                      <motion.p
                        className="text-3xl font-bold text-blue-600 dark:text-blue-400 tabular-nums"
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.5 }}
                      >
                        {(dashboardData.dashboard.water_temp / 10).toFixed(1)}°C
                      </motion.p>
                    ) : (
                      <p className="text-sm text-gray-400 dark:text-gray-500">No data</p>
                    )}
                  </div>
                  <div className="flex flex-col items-center gap-3 justify-center p-4 rounded-xl border border-amber-200/50 bg-amber-50/70 dark:bg-orange-900/20 backdrop-blur-sm">
                    <FiThermometer className="h-6 w-6 text-amber-600" />
                    <span className="text-xm font-medium text-gray-500 dark:text-gray-400">Ambient</span>
                    {dashboardData.ambientTemp.ambient_temp !== undefined ? (
                      <motion.p
                        className="text-3xl font-bold text-amber-600 dark:text-orange-400 tabular-nums"
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.5 }}
                      >
                        {(dashboardData.ambientTemp.ambient_temp / 100).toFixed(1)}°C
                      </motion.p>
                    ) : (
                      <p className="text-sm text-gray-400 dark:text-gray-500">No data</p>
                    )}
                  </div>
                </div>
                
                {/* Heater status rows */}
                
                {(() => {
                  const isCombinedHeaterDevice = dashboardData?.auxAssign?.equipment?.some((aux) => aux.type_code === AuxTypeCode.COMBINE_POOL_SPA_HEATER) || false;
                  const hasPoolHeater = isCombinedHeaterDevice || (dashboardData?.auxAssign?.equipment?.some((aux) => aux.type_code === AuxTypeCode.POOL_HEATER) || false);
                  const hasSpaHeater = dashboardData?.auxAssign?.equipment?.some((aux) => aux.type_code === AuxTypeCode.SPA_HEATER) || false;
                  const hasSolarHeater = isCombinedHeaterDevice || (dashboardData?.auxAssign?.equipment?.some((aux) => aux.type_code === AuxTypeCode.SOLAR_DEDICATED || aux.type_code === AuxTypeCode.SOLAR_FILTER_PUMP) || false);

                  const poolTemp = dashboardData?.temperature?.temperature?.find((t) => t.category === 'pool');
                  const poolOn = !!(poolTemp?.is_pool_heater_on ?? false);
                  const spaOn = !!(poolTemp?.is_spa_heater_on ?? false);
                  const solarOn = !!(poolTemp?.is_solar_heater_on ?? false);

                  if (!hasPoolHeater && !hasSpaHeater && !hasSolarHeater) {
                    return null;
                  }

                  return (
                    <div className="mt-4 flex items-center justify-between gap-3">
                      {hasSpaHeater && (
                        <div className="flex items-center gap-2">
                          <span className={`h-2.5 w-2.5 rounded-full ${spaOn ? 'bg-green-500' : 'bg-gray-400 dark:bg-gray-600'}`} />
                          <span className="text-xm font-medium text-gray-600 dark:text-gray-300">Spa Heater</span>
                        </div>
                      )}
                      {hasSolarHeater && (
                        <div className="flex items-center gap-2">
                          <span className={`h-2.5 w-2.5 rounded-full ${solarOn ? 'bg-green-500' : 'bg-gray-400 dark:bg-gray-600'}`} />
                          <span className="text-xm font-medium text-gray-600 dark:text-gray-300">Solar Heater</span>
                        </div>
                      )}
                      {hasPoolHeater && (
                        <div className="flex items-center gap-2">
                          <span className={`h-2.5 w-2.5 rounded-full ${poolOn ? 'bg-green-500' : 'bg-gray-400 dark:bg-gray-600'}`} />
                          <span className="text-xm font-medium text-gray-600 dark:text-gray-300">Pool Heater</span>
                        </div>
                      )}
                    </div>
                  );
                })()}
                </>
              ) : (
                <div className="flex flex-col items-center justify-center p-8 rounded-lg bg-gray-50/70 dark:bg-gray-800/50 border border-dashed border-gray-200 dark:border-gray-700 backdrop-blur-sm">
                  <FiThermometer className="text-3xl text-gray-300 dark:text-gray-600 mb-2" />
                  <p className="text-gray-500 dark:text-gray-400">No temperature data available</p>
                  <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">Temperature information will appear here once available</p>
                </div>
              )}
            </div>
          </Card>

          {/* Chemistry Card */}
          <Card className="group">
            <div>
              <div className="flex items-center justify-between mb-3">
                <h5 className="font-semibold">Chemistry</h5>
                <div className="flex items-center space-x-2">
                  {dashboardData?.dashboard?.chemistry_stable !== undefined && (
                    <div className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${dashboardData.dashboard.chemistry_stable
                        ? 'bg-emerald-100/80 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400'
                        : 'bg-amber-100/80 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400'
                      }`}>
                      {dashboardData.dashboard.chemistry_stable ? 'Stable' : 'Unstable'}
                    </div>
                  )}
                  <button
                    onClick={() => toggleSidebar('chemistry')}
                    className={classNames(
                      "p-1.5 rounded-full transition-colors",
                      {
                        "hover:bg-gray-100 text-gray-500": mode === 'light',
                        "hover:bg-gray-800 text-gray-400": mode === 'dark'
                      }
                    )}
                    aria-label="View chemistry details"
                  >
                    <FiChevronRight size={18} className="transition-transform group-hover:translate-x-1" />
                  </button>
                </div>
              </div>

              {dashboardData?.dashboard &&
                (dashboardData.dashboard.actual_ph !== undefined ||
                  dashboardData.dashboard.actual_orp !== undefined) ? (
                <div className="grid grid-cols-2 gap-8">


                  {/* ORP Value Circle */}
                  <div className="flex flex-col items-center gap-3">
                    <div className="relative w-28 h-28 bg-white/50 dark:bg-gray-800/30 rounded-full p-1 backdrop-blur-sm border border-gray-200/60 dark:border-gray-700/40">
                      {dashboardData.dashboard.actual_orp !== undefined ? (
                        <CircularProgressbarWithChildren
                          value={dashboardData.dashboard.is_naked ? 50 : ((dashboardData.dashboard.actual_orp / (dashboardData.dashboard.d_orp_mineral ? dashboardData.dashboard.d_orp_mineral * 2 : 700)) * 100)}
                          strokeWidth={10}
                          styles={buildStyles({
                            strokeLinecap: 'butt',
                            pathColor: orpColor,
                            trailColor: mode === 'light' ? '#E2E8F0' : '#2D3748',
                            pathTransition: 'stroke-dashoffset 1s ease-in-out',
                            rotation: 0.63,
                            pathTransitionDuration: 1,
                          })}
                          circleRatio={0.75}
                        >
                          <div className="flex flex-col items-center justify-center">
                            {dashboardData.dashboard.is_naked ? (
                              <span className="text-xs text-gray-500 dark:text-gray-400">-</span>
                            ) : dashboardData.dashboard.main_pump_status === 0 ? (
                              <motion.span
                                className="text-xl font-bold text-gray-500 dark:text-gray-400"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: 0.5, duration: 0.5 }}
                              >
                                OFF
                              </motion.span>
                            ) : (
                              <>
                                <motion.span
                                  className="text-2xl font-bold"
                                  style={{ color: orpColor }}
                                  initial={{ opacity: 0 }}
                                  animate={{ opacity: 1 }}
                                  transition={{ delay: 0.5, duration: 0.5 }}
                                >
                                  {dashboardData.dashboard.actual_orp}
                                </motion.span>
                                <span className="text-sm text-gray-500 dark:text-gray-400">mV</span>
                              </>
                            )}
                          </div>
                        </CircularProgressbarWithChildren>
                      ) : (
                        <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-50/70 dark:bg-gray-800/50 rounded-full">
                          <span className="text-sm text-gray-400 dark:text-gray-500">No data</span>
                        </div>
                      )}
                    </div>
                    <p className="mt-1 text-sm font-semibold text-gray-700 dark:text-gray-300">ORP Value</p>
                    {!dashboardData.dashboard.is_naked && (
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Target: {hasLiquidChlorine ? dashboardData?.dashboard?.d_orp_liquid : dashboardData?.dashboard?.d_orp_mineral || 0} mV
                      </p>
                    )}
                  </div>

                  {/* pH Value Circle */}
                  <div className="flex flex-col items-center gap-3">
                    <div className="relative w-28 h-28 bg-white/50 dark:bg-gray-800/30 rounded-full p-1 backdrop-blur-sm border border-gray-200/60 dark:border-gray-700/40">
                      {dashboardData.dashboard.actual_ph !== undefined ? (
                        <CircularProgressbarWithChildren
                          value={(dashboardData.dashboard.actual_ph / 140) * 100}
                          strokeWidth={10}
                          styles={buildStyles({
                            strokeLinecap: 'butt',
                            pathColor: phColor,
                            trailColor: mode === 'light' ? '#E2E8F0' : '#2D3748',
                            pathTransition: 'stroke-dashoffset 1s ease-in-out',
                            rotation: 0.63,
                            pathTransitionDuration: .1,
                          })}
                          circleRatio={0.75}
                        >
                          <div className="flex flex-col items-center justify-center">
                            {dashboardData.dashboard.main_pump_status === 0 ? (
                              <motion.span
                                className="text-xl font-bold text-gray-500 dark:text-gray-400"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: 0.5, duration: 0.5 }}
                              >
                                OFF
                              </motion.span>
                            ) : (
                              <>
                                <motion.span
                                  className="text-2xl font-bold"
                                  style={{ color: phColor }}
                                  initial={{ opacity: 0 }}
                                  animate={{ opacity: 1 }}
                                  transition={{ delay: 0.5, duration: 0.5 }}
                                >
                                  {(dashboardData.dashboard.actual_ph / 10).toFixed(1)}
                                </motion.span>
                                <span className="text-sm text-gray-500 dark:text-gray-400">pH</span>
                              </>
                            )}
                          </div>
                        </CircularProgressbarWithChildren>
                      ) : (
                        <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-50/70 dark:bg-gray-800/50 rounded-full">
                          <span className="text-sm text-gray-400 dark:text-gray-500">No data</span>
                        </div>
                      )}
                    </div>
                    <p className="mt-1 text-sm font-semibold text-gray-700 dark:text-gray-300">pH Value</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Target: {(dashboardData.dashboard.d_ph / 10).toFixed(1)}</p>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center p-8 rounded-lg bg-gray-50/70 dark:bg-gray-800/50 border border-dashed border-gray-200 dark:border-gray-700 backdrop-blur-sm">
                  <FiDroplet className="text-3xl text-gray-300 dark:text-gray-600 mb-2" />
                  <p className="text-gray-500 dark:text-gray-400">No chemistry data available</p>
                  <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">Chemistry readings will appear here once available</p>
                </div>
              )}
            </div>
          </Card>
        </div>

        {/* Savings Card */}
        <Card>
          <div>
            <h5 className="font-semibold mb-4">Savings</h5>
            {dashboardData?.dashboard && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center gap-3 p-3 bg-gray-50/70 dark:bg-gray-800/50 rounded-lg backdrop-blur-sm">
                  <div className="text-pink-500">
                    <svg width="30" height="28" viewBox="0 0 30 27.832" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M0.534,11.555l1.6,-0.687 0.609,-0.261A10.64,10.64 0,0 1,5.566 7.35L5.566,3.633a0.879,0.879 0,0 1,0.879 -0.879,4.4 4.4,0 0,1 2.379,0.705 5.1,5.1 0,0 1,9.925 1.58,12.948 12.948,0 0,1 5.91,3.454 9.309,9.309 0,0 1,2.435 4.471,2.228 2.228,0 0,0 0.868,-3.03 0.879,0.879 0,1 1,1.537 -0.854,3.983 3.983,0 0,1 -2.217,5.712 9.573,9.573 0,0 1,-4.575 7.828q-0.524,2.265 -1.049,4.53a0.879,0.879 0,0 1,-0.856 0.68L17.93,27.83a0.879,0.879 0,0 1,-0.856 -0.681l-0.524,-2.266a16.144,16.144 0,0 1,-4.174 0q-0.263,1.135 -0.526,2.27a0.879,0.879 0,0 1,-0.856 0.68L8.122,27.833a0.879,0.879 0,0 1,-0.856 -0.681L6.952,25.8l-0.742,-3.2A10.83,10.83 0,0 1,2.749 18.87l-0.668,-0.286L0.532,17.92A0.879,0.879 0,0 1,0 17.112L0,12.364a0.879,0.879 0,0 1,0.534 -0.808ZM13.653,1.755a3.34,3.34 0,0 0,-1.89 6.094h3.779a3.34,3.34 0,0 0,-1.89 -6.094ZM1.758,16.533l1.015,0.435 0.956,0.409a0.879,0.879 0,0 1,0.44 0.415,9.074 9.074,0 0,0 3.291,3.52 0.879,0.879 0,0 1,0.384 0.543q0.411,1.773 0.821,3.546l0.156,0.673h1.474l0.417,-1.8 0.136,-0.586a0.879,0.879 0,0 1,1.019 -0.666,14.34 14.34,0 0,0 5.194,0.006 0.879,0.879 0,0 1,1.017 0.666l0.551,2.382L20.1,26.076q0.486,-2.1 0.971,-4.194a0.879,0.879 0,0 1,0.387 -0.545,7.936 7.936,0 0,0 4.064,-6.6c0,-3.557 -2.811,-6.686 -7.065,-7.952a5.149,5.149 0,0 1,-0.623 1.217,0.879 0.879,0 0,1 -0.493,1.607L9.961,9.609A0.879,0.879 0,0 1,9.468 8a5.122,5.122 0,0 1,-0.888 -2.4l-0.011,-0.014a2.661,2.661 0,0 0,-1.245 -0.925v3.125A0.879,0.879 0,0 1,6.959 8.5a8.9,8.9 0,0 0,-2.791 3.181,0.879 0.879,0 0,1 -0.44,0.415l-0.9,0.384 -1.074,0.46v3.589Z" fill="#f95f8f"/>
                    </svg>
                  </div>
                  <div className="flex-grow">
                    <p className="font-medium">Total Savings</p>
                    <p className="text-xl font-bold">
                      {isVSDPump(dashboardData.pump?.pump_type)
                        ? `$ ${Number(dashboardData.dashboard?.cost_savings?.[3] ?? 0).toFixed(1)}`
                        : "Require VSD"}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-gray-50/70 dark:bg-gray-800/50 rounded-lg backdrop-blur-sm">
                  <div className="text-green-500">
                    <svg width="31" height="28" viewBox="0 0 30.465 27.685" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12.267,3.049C9.623,0.406 4.449,0.1 0.787,0.1A0.687,0.687 0,0 0,0.1 0.787c0,3.662 0.305,8.836 2.949,11.48a6.4,6.4 0,0 0,4.523 1.615,6.854 6.854,0 0,0 4.7,-1.616C14.237,10.297 14.6,5.378 12.267,3.049ZM11.3,11.296c-1.47,1.469 -5.553,1.721 -7.274,0 -2.134,-2.134 -2.5,-6.47 -2.544,-9.817 3.348,0.04 7.683,0.409 9.817,2.544C13.016,5.743 12.765,9.827 11.3,11.296Z" fill="#5de878" stroke="#25d9cc" strokeWidth="0.2"/>
                      <path d="M29.678,2.818c-3.662,0 -8.836,0.305 -11.48,2.949 -2.331,2.329 -1.969,7.248 0,9.217a6.856,6.856 0,0 0,4.694 1.615,6.407 6.407,0 0,0 4.524,-1.613c2.643,-2.644 2.949,-7.818 2.949,-11.48A0.687,0.687 0,0 0,29.678 2.818ZM26.441,14.012c-1.721,1.721 -5.8,1.469 -7.274,0s-1.721,-5.553 0,-7.274c2.134,-2.134 6.47,-2.5 9.817,-2.544C28.945,7.542 28.576,11.88 26.441,14.012Z" fill="#5de878" stroke="#25d9cc" strokeWidth="0.2"/>
                      <path d="M24.716,8.77a0.742,0.742 0,0 0,-0.969 -0.3,16.469 16.469,0 0,0 -9.036,11.24c-1.455,-9.444 -5.665,-12.323 -7.45,-13.544a4.316,4.316 0,0 1,-0.492 -0.359,0.687 0.687,0 1,0 -0.972,0.972 5.528,5.528 0,0 0,0.688 0.521c2.012,1.376 7.356,5.032 7.356,19.606a0.687,0.687 0,0 0,1.374 0c0,-5.889 1.582,-13.652 9.115,-17.192c0,0 0.111,-0.054 0.111,-0.054A0.637,0.637 0,0 0,24.716 8.77Z" fill="#5de878" stroke="#25d9cc" strokeWidth="0.2"/>
                    </svg>
                  </div>
                  <div className="flex-grow">
                    <p className="font-medium">Hourly Savings</p>
                    <p className="text-xl font-bold">
                      {isVSDPump(dashboardData.pump?.pump_type)
                        ? `$ ${Number(dashboardData.dashboard?.cost_savings?.[0] ?? 0).toFixed(1)} /Hour`
                        : "Require VSD"}
                    </p>
                  </div>
                </div>
              </div>
            )}
            {!dashboardData?.dashboard && (
              <div className="flex flex-col items-center justify-center p-8 rounded-lg bg-gray-50/70 dark:bg-gray-800/50 border border-dashed border-gray-200 dark:border-gray-700 backdrop-blur-sm">
                <FiActivity className="text-3xl text-gray-300 dark:text-gray-600 mb-2" />
                <p className="text-gray-500 dark:text-gray-400">No savings data available</p>
                <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">Savings information will appear here once available</p>
              </div>
            )}
          </div>
        </Card>

        {/* Auxiliary Devices */}
        {dashboardData?.manual?.aux ? (
          dashboardData.manual.aux.length > 0 ? (
            <Card>
              <div>
                <h5 className="font-semibold mb-4">Auxiliary Devices</h5>

                {/* Active Devices */}
                {(() => {
                  const activeDevices = dashboardData.manual.aux.filter(device => device.aux_status == true);
                  if (activeDevices.length > 0) {
                    return (
                      <>
                        <div className="flex items-center mb-3">
                          <div className="h-2 w-2 rounded-full bg-green-500 mr-2"></div>
                          <p className="text-sm font-medium text-gray-600 dark:text-gray-300">Active Devices</p>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 auto-rows-auto mb-6">
                          {activeDevices
                            .slice()
                            .map((device, index) => {
                              const deviceSchedules = dashboardData?.scheduleInfo?.schedules
                                ?.filter((schedule) =>
                                  schedule.aux_type_code === device.aux_type_code &&
                                  schedule.aux_slot === device.aux_slot_num
                                )
                                .map((schedule) => ({
                                  ID: schedule.schedule_id,
                                  DeviceID: selectedDeviceId || '',
                                  Index: schedule.schedule_num,
                                  Name: schedule.schedule_name,
                                  PumpSpeed: schedule.pump_speed,
                                  AuxType: schedule.aux_type_code,
                                  AuxSlot: schedule.aux_slot,
                                  IsScheduleOn: schedule.schedule_state,
                                  StartTime: schedule.start_time,
                                  EndTime: schedule.end_time,
                                  weekdays_array: schedule.week_days,
                                })) || [];

                              // Create device object compatible with DeviceCard
                              const deviceObj = {
                                ID: index,
                                DeviceID: selectedDeviceId || '',
                                Name: device.aux_tag,
                                Slot: device.aux_slot_num || index,
                                Type: device.aux_type_code || 0,
                                IsTrump: false,
                                IsOn: device.aux_status
                              };

                              return (
                                <div key={index} className="h-auto self-start">
                                  <DeviceCard
                                    key={index}
                                    device={{
                                      ...deviceObj,
                                      icon: getAuxIcon(device.aux_type_code || 0),
                                      schedules: deviceSchedules
                                    }}
                                    isSelected={expandedDeviceIds.includes(index)}
                                    onClick={() => toggleDeviceExpanded(index)}
                                  />
                                </div>
                              );
                            })}
                        </div>
                      </>
                    );
                  }
                  return null;
                })()}

                {/* Inactive Devices */}
                {(() => {
                  const inactiveDevices = dashboardData.manual.aux.filter(device => device.aux_status === false && device.aux_type_code !== AuxTypeCode.SPALSHME_PUMP);
                  if (inactiveDevices.length > 0) {
                    return (
                      <>
                        <div className="flex items-center mb-3">
                          <div className="h-2 w-2 rounded-full bg-gray-400 mr-2"></div>
                          <p className="text-sm font-medium text-gray-600 dark:text-gray-300">Inactive Devices</p>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 auto-rows-auto">
                          {inactiveDevices
                            .slice()
                            .map((device, index) => {
                              const deviceSchedules = dashboardData?.scheduleInfo?.schedules
                                ?.filter((schedule) =>
                                  schedule.aux_type_code === device.aux_type_code &&
                                  schedule.aux_slot === device.aux_slot_num
                                )
                                .map((schedule) => ({
                                  ID: schedule.schedule_id,
                                  DeviceID: selectedDeviceId || '',
                                  Index: schedule.schedule_num,
                                  Name: schedule.schedule_name,
                                  PumpSpeed: schedule.pump_speed,
                                  AuxType: schedule.aux_type_code,
                                  AuxSlot: schedule.aux_slot,
                                  IsScheduleOn: schedule.schedule_state,
                                  StartTime: schedule.start_time,
                                  EndTime: schedule.end_time,
                                  weekdays_array: schedule.week_days,
                                })) || [];

                              // Create device object compatible with DeviceCard
                              const deviceObj = {
                                ID: index + 100, // Offset to avoid ID conflicts with active devices
                                DeviceID: selectedDeviceId || '',
                                Name: device.aux_tag,
                                Slot: device.aux_slot_num || index,
                                Type: device.aux_type_code || 0,
                                IsTrump: false,
                                IsOn: false
                              };

                              return (
                                <div key={index + 100} className="h-auto self-start">
                                  <DeviceCard
                                    key={index + 100}
                                    device={{
                                      ...deviceObj,
                                      icon: getAuxIcon(device.aux_type_code || 0),
                                      schedules: deviceSchedules
                                    }}
                                    isSelected={expandedDeviceIds.includes(index + 100)}
                                    onClick={() => toggleDeviceExpanded(index + 100)}
                                  />
                                </div>
                              );
                            })}
                        </div>
                      </>
                    );
                  }
                  return null;
                })()}
              </div>
            </Card>
          ) : (
            <Card>
              <div>
                <h5 className="font-semibold mb-4">Auxiliary Devices</h5>
                <div className="flex flex-col items-center justify-center p-8 rounded-lg bg-gray-50/70 dark:bg-gray-800/50 border border-dashed border-gray-200/50 dark:border-gray-700/30 backdrop-blur-sm">
                  <FiSettings className="text-3xl text-gray-300 dark:text-gray-600 mb-2" />
                  <p className="text-gray-500 dark:text-gray-400">No auxiliary devices found</p>
                  <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">Auxiliary devices will appear here once configured</p>
                </div>
              </div>
            </Card>
          )
        ) : null}
      </div>

      {/* Sidebars */}
      <Sidebar 
        isOpen={activeSidebar === 'chemistry'}
        onClose={closeSidebar}
        title="Chemistry Controls"
        isLoading={sidebarLoading}
      >
        {dashboardData && chemistryData && (
          <ChemistrySidebar
            dashboardData={dashboardData.dashboard}
            auxDevices={dashboardData.manual?.aux || []}
            chemistryData={chemistryData}
          />
        )}
      </Sidebar>

      <Sidebar 
        isOpen={activeSidebar === 'temperature'}
        onClose={closeSidebar}
        title="Temperature Controls"
        isLoading={sidebarLoading}
      >
        {dashboardData && (
          <TemperatureSidebar
            auxAssign={dashboardData.auxAssign}
          />
        )}
      </Sidebar>

      {/* <Sidebar 
        isOpen={activeSidebar === 'pumpSpeed'} 
        onClose={closeSidebar}
        title="Pump Speed Settings"
      >
        <PumpSpeedSidebar 
          pumpConfig={dashboardData?.general_config ? {
            CurrentSchedulePumpSpeed: dashboardData.general_config.CurrentSchedulePumpSpeed,
            ManualPumpSpeed: dashboardData.general_config.ManualPumpSpeed,
            MinAllowPumpSpeed: dashboardData.general_config.MinAllowPumpSpeed,
            HeaterPumpSpeed: dashboardData.general_config.HeaterPumpSpeed,
            SolarHeatingPumpSpeed: dashboardData.general_config.SolarHeatingPumpSpeed,
            CleanerPumpSpeed: dashboardData.general_config.CleanerPumpSpeed,
            PumpType: dashboardData.general_config.PumpType
          } : undefined}
        />
      </Sidebar> */}
    </div>
  );
};

export default DashboardPage;
